import { FC, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { AutocompleteRenderInputParams, FormInputText, GroupedSearchDropdown } from '@hopskipdrive/react-components';
import { useFlags } from '@rideiq/contexts/LDContext';
import { includes } from 'lodash';

import { RiderCreationModal } from '../../../riders/features/RiderCreationModal';

import { useRefetchRidersOnTabFocus } from './useRefetchRidersOnTabFocus';

type Option = {
  group: string;
  label: string;
  [x: string]: string | number | boolean | string[] | object[];
};

interface SelectRiderProps {
  options: Option[];
  waypointIndex: number;
  placeholder: string;
  isAnchorWaypoint: boolean;
  rideType: string;
  addRiderToRide: (rider: Option) => void;
  deboucedQueryRiders: (query: string) => void;
  canAddRiders: boolean;
  onSelect: () => void;
  isSeries: boolean;
}

const RIDERS_ON_THIS_RIDE_LABEL = 'Riders on this Ride:';
const CREATE_NEW_RIDER_LABEL = 'Create a new rider:';
const SAVED_RIDER_LABEL = 'Saved riders:';

export const SelectRider: FC<SelectRiderProps> = ({
  options,
  waypointIndex,
  placeholder,
  isAnchorWaypoint,
  rideType,
  addRiderToRide,
  deboucedQueryRiders,
  canAddRiders,
  onSelect,
  isSeries,
}) => {
  const [riderInputValue, setRiderInputValue] = useState<string>('');

  const {
    getValues,
    setValue,
    clearErrors,
    control,
    formState: { errors },
  } = useFormContext();
  const rideIsFull =
    options.reduce((count, option) => (option.group == RIDERS_ON_THIS_RIDE_LABEL ? count + 1 : count), 0) === 4;
  const [riderToCreate, setRiderToCreate] = useState({ isOpen: false, firstName: '', lastName: '' });
  const { iepStructuredNotesEnabled } = useFlags();
  const useNewRiderForm = iepStructuredNotesEnabled;

  useRefetchRidersOnTabFocus(useNewRiderForm, riderInputValue, deboucedQueryRiders);

  const watchWaypoints: WaypointFormType[] = useWatch({
    control,
    name: 'waypointsAttributes',
    defaultValue: [],
  });

  const handleSelectRider = (selectedRiderOption) => {
    const lastRiderWaypointIndex = watchWaypoints.findIndex(
      (waypoint) => !waypoint.anchor && includes(waypoint.passengerIds, selectedRiderOption.id)
    );
    let nextWaypointIndex;
    let nextWaypointPassengerIds;

    // check if the selected rider was already on this ride
    // this means that the rider is being moved to another waypoint
    if (lastRiderWaypointIndex !== -1) {
      // removes the rider from the last waypoint it was present
      nextWaypointIndex = lastRiderWaypointIndex;
      nextWaypointPassengerIds = watchWaypoints[lastRiderWaypointIndex]?.passengerIds?.filter(
        (passengerId) => passengerId !== selectedRiderOption.id
      );
    } else {
      // otherwise this is a new rider to the ride
      // this is for also adding to the first non-anchor waypoint or to the anchor waypoint
      // depends if the current waypoint is anchor and on rideType(the anchor index changes)
      if (rideType === 'pick_up') {
        nextWaypointIndex = isAnchorWaypoint ? 1 : 0;
      } else {
        nextWaypointIndex = isAnchorWaypoint ? 0 : watchWaypoints.length - 1;
      }
      nextWaypointPassengerIds = [...(watchWaypoints[nextWaypointIndex]?.passengerIds || []), selectedRiderOption.id];
      addRiderToRide(selectedRiderOption);
    }
    // updates the selected passengers for the current waypoint
    setValue(
      `waypointsAttributes.${waypointIndex}.passengerIds`,
      [...(watchWaypoints[waypointIndex]?.passengerIds || []), selectedRiderOption.id].filter((value) => value)
    );
    setValue(
      `waypointsAttributes.${nextWaypointIndex}.passengerIds`,
      nextWaypointPassengerIds.filter((value) => value)
    );

    clearErrors(`waypointsAttributes.${waypointIndex}.passengerIds`);
    clearErrors(`waypointsAttributes.${nextWaypointIndex}.passengerIds`);

    onSelect();
  };

  return (
    <>
      <GroupedSearchDropdown
        ListboxProps={{ 'aria-label': placeholder, 'aria-labelledby': undefined }}
        options={[
          ...options,
          ...(riderInputValue && !rideIsFull ? [{ label: riderInputValue, group: CREATE_NEW_RIDER_LABEL }] : []),
        ]}
        isLoading={false}
        disabled={!canAddRiders}
        inputValue={riderInputValue}
        setInputValue={(value) => {
          setRiderInputValue(value);
          deboucedQueryRiders('');
        }}
        onChange={(_, value: Option | string | undefined) => {
          if (!value || typeof value === 'string') {
            return;
          }
          switch (value.group) {
            case RIDERS_ON_THIS_RIDE_LABEL:
            case SAVED_RIDER_LABEL:
              handleSelectRider(value);
              break;
            case CREATE_NEW_RIDER_LABEL: {
              const { firstName, lastName } = getFirstLastNames(value.label.trim());

              if (useNewRiderForm) {
                // Leave the input and search results untouched so the user can either
                // re-search for the rider they just created, or reopen this same option.
                window.open(buildInlineRiderCreationUrl(firstName, lastName, isSeries), '_blank');

                return;
              }

              setRiderToCreate({ isOpen: true, firstName, lastName });

              break;
            }
          }
          setRiderInputValue('');
          deboucedQueryRiders('');
        }}
        getOptionDisabled={(option: Option) => {
          const currentPassengerIds = getValues(`waypointsAttributes.${waypointIndex}.passengersIds`);

          if (option.group === SAVED_RIDER_LABEL && rideIsFull) {
            return true;
          }

          return option.group === RIDERS_ON_THIS_RIDE_LABEL && includes(currentPassengerIds, option.id);
        }}
        renderInput={(params: AutocompleteRenderInputParams) => (
          <FormInputText
            {...params}
            className="select-rider"
            variant="outlined"
            fullWidth
            placeholder={placeholder}
            inputProps={{ ...params.inputProps, 'aria-label': placeholder }}
            onChange={(e) => {
              deboucedQueryRiders(e.target.value);
            }}
            error={!!(errors.waypointsAttributes && errors.waypointsAttributes[waypointIndex]?.passengerIds)}
            helperText={errors.waypointsAttributes && errors.waypointsAttributes[waypointIndex]?.passengerIds?.message}
            sx={{
              minWidth: '262px',
            }}
          />
        )}
        boldCharacterMatch={true}
      />
      {!useNewRiderForm && (
        <RiderCreationModal
          isOpen={riderToCreate.isOpen}
          handleClose={() => setRiderToCreate({ isOpen: false, firstName: '', lastName: '' })}
          riderForRide={true}
          defaultState={{ firstName: riderToCreate.firstName, lastName: riderToCreate.lastName }}
          handleSelectRider={handleSelectRider}
        />
      )}
    </>
  );
};

function getFirstLastNames(names) {
  const lastNameIndex = names.lastIndexOf(' ');

  if (lastNameIndex === -1) {
    return { firstName: names, lastName: '' };
  }

  return { firstName: names.substring(0, lastNameIndex), lastName: names.substring(lastNameIndex + 1) };
}

const CREATE_RIDER_ORIGIN = 'ride_creation';

function buildInlineRiderCreationUrl(firstName: string, lastName: string, isSeries: boolean): string {
  const params = new URLSearchParams({ origin: CREATE_RIDER_ORIGIN });

  if (firstName) {
    params.set('firstName', firstName);
  }
  if (lastName) {
    params.set('lastName', lastName);
  }
  if (isSeries) {
    params.set('rideContext', 'ride_series');
  }

  return `/riders/new?${params.toString()}`;
}
